import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { LoaderCircle } from "lucide-react";
import {
  Form,
  Link,
  type MetaFunction,
  useActionData,
  useLoaderData,
} from "react-router";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";

import { Google } from "@/auth/icons/google";
import type { LoginLoaderResponse } from "@/auth/login";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { useIsPending } from "@/utils/form";

export const meta: MetaFunction = () => {
  return [{ title: `Sign in or create an account | ${publicEnv.siteTitle}` }];
};

const emailFormSchema = z.object({
  email: z.string().max(256).email("Enter a valid email address."),
});

// Present account creation and returning login as one honest passwordless flow.
export default function Login() {
  const { authEmail, authError } = useLoaderData<LoginLoaderResponse>();
  const actionData = useActionData<{ error?: string }>();
  const isEmailPending = useIsPending();
  const isGooglePending = useIsPending({ formAction: "/auth/google" });
  const isPending = isEmailPending || isGooglePending;

  const [emailForm, { email }] = useForm({
    constraint: getZodConstraint(emailFormSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: emailFormSchema });
    },
    shouldRevalidate: "onInput",
  });

  return (
    <div>
      <p className="text-primary font-mono text-xs font-semibold tracking-[0.14em] uppercase">
        Welcome
      </p>
      <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight">
        Sign in or create your ledger.
      </h1>
      <p className="text-muted-foreground mt-3 leading-7">
        Continue with email or Google. We&apos;ll sign you in if your account
        exists, or guide you through a short family setup if it does not.
      </p>

      <div className="mt-8 grid gap-5">
        {actionData?.error && (
          <p
            className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border px-4 py-3 text-sm"
            role="alert"
          >
            {actionData.error}
          </p>
        )}

        <Form action="/auth/google" method="POST">
          <Button
            className="h-12 w-full"
            disabled={isPending}
            variant="outline"
          >
            {isGooglePending ? (
              <LoaderCircle className="size-5 animate-spin" />
            ) : (
              <Google className="size-5" />
            )}
            Continue with Google
          </Button>
        </Form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-3">or</span>
          </div>
        </div>

        <Form className="grid gap-4" method="POST" {...getFormProps(emailForm)}>
          <HoneypotInputs />
          <div className="grid gap-2">
            <Label htmlFor={email.id}>Email address</Label>
            <Input
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              className="h-12"
              defaultValue={authEmail || ""}
              disabled={isPending}
              placeholder="you@example.com"
              error={
                (!authError && email.errors) || (!authEmail && authError)
                  ? email.errors?.join(" ") || authError?.message
                  : ""
              }
              {...getInputProps(email, { type: "email" })}
            />
          </div>

          <Button className="h-12 w-full" disabled={isPending}>
            {isEmailPending && <LoaderCircle className="size-5 animate-spin" />}
            Email me a secure link
          </Button>
        </Form>
      </div>

      <p className="text-muted-foreground mt-7 text-sm leading-6">
        By continuing, you agree to the{" "}
        <Link className="underline-decoration underline" to="/terms">
          Terms
        </Link>{" "}
        and{" "}
        <Link className="underline-decoration underline" to="/privacy">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
